import { getFailureTypeColor } from "@/lib/failedInvoiceTypes";
import { getInvoiceCategoryColor, INVOICE_CATEGORY } from "@/lib/invoiceCategories";

/**
 * Semantic + legacy status colors for Invoice Manager.
 * Role tokens resolve from CSS custom properties in index.css.
 */

const token = (name) => ({
  fg: `var(--semantic-${name}-fg)`,
  muted: `var(--semantic-${name}-fg)`,
  bg: `var(--semantic-${name}-bg)`,
  border: `var(--semantic-${name}-border)`,
});

export const SEMANTIC = {
  brand: token("brand"),
  info: token("info"),
  approval: token("approval"),
  review: token("review"),
  ai: token("ai"),
  success: token("success"),
  warning: token("warning"),
  processing: token("processing"),
  danger: token("danger"),
  query: token("query"),
  neutral: token("neutral"),
  legacySuccess: token("legacy-success"),
  ready: token("ready"),
  caution: token("caution"),
};

/** Action buttons → semantic role (Operate-mode color coding) */
export const ACTION_SEMANTIC = {
  edit: "brand",
  view: "neutral",
  delete: "danger",
  comment: "approval",
  more: "neutral",
  logout: "danger",
  // Invoice record actions. Each takes a distinct role so an icon-only control
  // is identifiable by colour before the tooltip arrives.
  rerun: "info",
  star: "caution",
  download: "neutral",
  back: "neutral",
};

/** Legacy display color per review status id (old app DataTable.css) */
export const REVIEW_STATUS_COLORS = {
  1: "#0263ff",
  2: "#ff7723",
  3: "#219653",
  4: "#8e30ff",
  5: "#6c57f2",
  6: "#319dff",
  7: "#219653",
  8: "#ff001f",
};

/** Legacy display color per processing status id (old app DataTable.css) */
export const PROCESSING_STATUS_COLORS = {
  1: "#0263ff",
  2: "#0d9488",
  3: "#f1ee16",
  4: "#ff7723",
  5: "#219653",
  6: "#ff001f",
  7: "#d68e01",
  8: "#a855f7",
  9: "#4d7c0f",
  10: "#c026d3",
};

/**
 * Colors for the overview tiles, expressed in terms of the status palette
 * above so the strip cannot drift away from the badges it summarises.
 */
export const OVERVIEW_STAT_COLORS = {
  // The "Under Review" tile filters on processing status id 2 (Under
  // Review), so it takes that status's colour — not the review-status
  // "Review Required" colour, which is a different field entirely.
  review: PROCESSING_STATUS_COLORS[2],
  successful: PROCESSING_STATUS_COLORS[5],
  failed: PROCESSING_STATUS_COLORS[6],
  // Read straight from the category/failure-type colour maps, rather than a
  // copy of their hex values, so the tile can never drift from the dot the
  // table and filter panel render for the same category/failure type.
  invoices: getInvoiceCategoryColor(INVOICE_CATEGORY.INVOICE),
  samples: getInvoiceCategoryColor(INVOICE_CATEGORY.SAMPLE_INVOICE),
  creditNotesCategory: getInvoiceCategoryColor(INVOICE_CATEGORY.CREDIT_NOTE),
  noPo: getFailureTypeColor("No PO"),
  creditNotesFailure: getFailureTypeColor("Credit Note"),
  foreignExchange: getFailureTypeColor("Foreign Exchange"),
  priceDifference: getFailureTypeColor("Price mismatch"),
  vatMismatch: getFailureTypeColor("VAT mismatch"),
};

export const REVIEW_STATUS_SEMANTIC = {
  1: "info",
  2: "info",
  3: "ai",
  4: "review",
  5: "success",
  6: "approval",
  7: "success",
  8: "danger",
};

export const PROCESSING_STATUS_SEMANTIC = {
  1: "info",
  2: "warning",
  3: "warning",
  4: "processing",
  5: "success",
  6: "danger",
  7: "query",
  8: "query",
  9: "warning",
  10: "info",
};

export function getSemantic(name) {
  return SEMANTIC[name] ?? SEMANTIC.neutral;
}

export function getSemanticFg(name) {
  return getSemantic(name).fg;
}

export function getReviewSemantic(id) {
  return REVIEW_STATUS_SEMANTIC[id] ?? "neutral";
}

export function getProcessingSemantic(id) {
  return PROCESSING_STATUS_SEMANTIC[id] ?? "neutral";
}

export function getReviewStatusColor(id) {
  return REVIEW_STATUS_COLORS[id] ?? SEMANTIC.neutral.fg;
}

export function getProcessingStatusColor(id) {
  return PROCESSING_STATUS_COLORS[id] ?? SEMANTIC.neutral.fg;
}

export function getColorStyle(color) {
  return {
    color,
    backgroundColor: `${color}1A`,
    borderColor: `${color}40`,
  };
}

/**
 * A label may arrive with no colour of its own, so this falls back to the
 * neutral role. `color-mix` rather than hex alpha suffixes because the fallback
 * is a `var()` token — "var(--x)1A" is not a colour, and a missing colour used
 * to leave the badge silently unstyled.
 */
export function getLabelBadgeStyle(color) {
  const base = color || SEMANTIC.neutral.fg;

  return {
    color: base,
    backgroundColor: `color-mix(in oklch, ${base}, transparent 90%)`,
    borderColor: `color-mix(in oklch, ${base}, transparent 60%)`,
  };
}

export function getStatusBadgeStyle(colorOrSemantic) {
  if (typeof colorOrSemantic === "string" && colorOrSemantic.startsWith("#")) {
    return getColorStyle(colorOrSemantic);
  }
  const semantic = getSemantic(colorOrSemantic);
  return {
    color: semantic.fg,
    backgroundColor: semantic.bg,
    borderColor: semantic.border,
  };
}

export function getStatusSurfaceStyle(
  colorOrSemantic,
  { active = false } = {},
) {
  const color =
    typeof colorOrSemantic === "string" && colorOrSemantic.startsWith("#")
      ? colorOrSemantic
      : getSemantic(colorOrSemantic).fg;

  return {
    backgroundColor: active ? `${color}14` : undefined,
  };
}

export function getStatCardStyle(semantic) {
  const role = getSemantic(semantic);
  return {
    backgroundColor: role.bg,
    borderColor: role.border,
  };
}

export function getActionButtonClass(action) {
  const semantic = ACTION_SEMANTIC[action] ?? "neutral";
  return `action-surface action-surface-${semantic}`;
}

export function getFilterOptionColor(semantic, legacyColor) {
  if (legacyColor) return legacyColor;
  return getSemanticFg(semantic);
}

function buildStatusEntry(id, label, getSemanticFn, getColorFn) {
  return {
    id,
    label,
    semantic: getSemanticFn(id),
    color: getColorFn(id),
  };
}

export function buildReviewStatus(id, label) {
  return buildStatusEntry(id, label, getReviewSemantic, getReviewStatusColor);
}

export function buildProcessingStatus(id, label) {
  return buildStatusEntry(
    id,
    label,
    getProcessingSemantic,
    getProcessingStatusColor,
  );
}
